'use client';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { darkTheme, theme } from '@/utils/theme';
import {
  DARK_MODE_CLASS,
  THEME_MODE_STORAGE_KEY,
  THEME_MODES,
} from './constants';

const { LIGHT, DARK } = THEME_MODES;

export type ThemeMode = (typeof THEME_MODES)[keyof typeof THEME_MODES];

type ThemeModeContextValue = {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

// Reads the class the no-flash script in layout.tsx set before first paint
function getInitialMode(): ThemeMode {
  if (typeof document === 'undefined') return LIGHT;
  return document.documentElement.classList.contains(DARK_MODE_CLASS)
    ? DARK
    : LIGHT;
}

export default function ThemeModeProvider({ children }: PropsWithChildren) {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);

  const setMode = (next: ThemeMode) => {
    setModeState(next);
    document.documentElement.classList.toggle(DARK_MODE_CLASS, next === DARK);
    try {
      localStorage.setItem(THEME_MODE_STORAGE_KEY, next);
    } catch {}
  };

  return (
    <ThemeModeContext.Provider value={{ mode, setMode }}>
      <ThemeProvider theme={mode === DARK ? darkTheme : theme}>
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
}
